import { addDays, dayLabel, parseDay, weekKey } from "./date";
import { stepBreakdown } from "./energy";
import { averagable, dayLedger, type DayLedger } from "./ledger";
import { dailySteps } from "./movement";
import { typeName } from "./plan";
import { hoursMinutes, signed } from "./progressView";
import { activeMinutes, treadmillMins } from "./training";
import type { KayaState } from "./types";
import { walkSecs } from "./walk";
import { drunkLitres, litres, mlText, waterEntries, waterTotal } from "./water";

/**
 * The records (ADR-061): every day of a window as a row with its exact figure, grouped Monday to
 * Sunday, newest first. A chart shows the shape; this is where a past day's number is read.
 *
 * Every day of the window is present, so a gap is seen as a gap ("–"), never skipped and never a
 * zero. Today is shown "so far" and is left out of every average and total, for the reason
 * Movement's and Energy's windows end yesterday: a figure still climbing drags an average down.
 */

export type RecordSeries = "steps" | "active" | "energy" | "water";

export interface RecordPart {
  label: string;
  value: string;
  /** an estimate: the row writes "~" before it */
  est?: boolean;
}

export interface RecordDay {
  ds: string;
  /** null: nothing measured or logged that day — never a zero */
  value: number | null;
  est?: boolean;
  /** where the figure came from, a mark, "so far" — the row's subtitle */
  note?: string;
  /** what the figure is made of: "walk 25 min", "Out ~2,310" */
  parts?: RecordPart[];
}

export interface RecordWeek {
  /** the week's Monday and Sunday; `days` holds only those inside the window */
  from: string;
  to: string;
  /** newest first */
  days: RecordDay[];
  total: number | null;
  avg: number | null;
  /** the days the total and average rest on */
  n: number;
  /** "Week of Mon 14 Sep · avg 7,120 · 49,840" */
  heading: string;
}

export interface Records {
  series: RecordSeries;
  title: string;
  unit: string;
  /** newest first */
  weeks: RecordWeek[];
  /** days in the window with a figure (today aside) */
  count: number;
  /** the window's first day */
  first: string | null;
  avg: number | null;
  total: number | null;
  /** days the window's figures could rest on — its length, today aside */
  of: number;
  /** energy only: the mean of In − Out over the averagable days, as Energy's hero has it */
  deficit: number | null;
}

export const SERIES_LABEL: Record<RecordSeries, { title: string; unit: string; back: string; short: string }> = {
  steps: { title: "Steps · every day", unit: "steps", back: "/progress/movement", short: "Steps" },
  active: { title: "Active time · every day", unit: "min", back: "/progress/training", short: "Active time" },
  energy: { title: "In and Out · every day", unit: "kcal", back: "/progress/energy", short: "In vs Out" },
  water: { title: "Drinks · every day", unit: "ml", back: "/food/water", short: "Drinks" },
};

export const RECORD_SERIES: RecordSeries[] = ["steps", "active", "energy", "water"];

export function isRecordSeries(s: string): s is RecordSeries {
  return (RECORD_SERIES as string[]).includes(s);
}

const grouped = (n: number) => Math.round(n).toLocaleString("en-US");

/** The first day anything was logged: the plan's start or the oldest log, whichever is earlier. */
function firstLogged(S: KayaState, today: string): string {
  const start = (S.profile.startDate || "").slice(0, 10);
  const oldest = Object.keys(S.logs || {})
    .filter((ds) => /^\d{4}-\d{2}-\d{2}$/.test(ds) && ds <= today)
    .sort()[0];
  const c = [start, oldest].filter((x): x is string => !!x && x <= today).sort();
  return c[0] ?? today;
}

/** "phone", "typed" or "phone + typed" — which count the day's figure rests on. */
function stepSource(S: KayaState, ds: string): string | undefined {
  const b = stepBreakdown(S, ds);
  if (b.device && b.manual) return "phone + typed";
  if (b.device) return "phone";
  if (b.manual) return "typed";
  return undefined;
}

/**
 * What a day's active time was made of, with durations read the way `training.ts` reads them —
 * the session's own clock, the extra clock, each walk's seconds, the treadmill's minutes.
 */
function activeParts(S: KayaState, ds: string): RecordPart[] {
  const L = S.logs[ds];
  if (!L) return [];
  const out: RecordPart[] = [];
  const mins = (ms: number) => `${Math.round(ms / 60000)} min`;
  if ((L.elapsedMs || 0) >= 30000) {
    const own =
      L.ran?.key ??
      Object.keys(L.done || {}).find((k) => k !== "rescue" && !k.startsWith("extra:") && Object.keys(L.done[k]).length);
    out.push({ label: own ? typeName(own) : "session", value: mins(L.elapsedMs || 0) });
  }
  const extraMs = Math.max(L.extraDoneMs || 0, L.extraElapsed || 0);
  if (extraMs >= 30000) {
    const label = L.extras?.length ? L.extras.map((k) => typeName(k)).join(" + ") : L.rescued ? "rescue" : "extra";
    out.push({ label, value: mins(extraMs) });
  }
  for (const w of L.walks || []) {
    if (w.secs >= 30) out.push({ label: "walk", value: mins(walkSecs([w]) * 1000) });
  }
  const tm = treadmillMins(L.treadmill);
  if (tm > 0) out.push({ label: "treadmill", value: `${Math.round(tm)} min` });
  return out;
}

function dayOf(S: KayaState, series: RecordSeries, ds: string, today: string, ledger?: DayLedger): RecordDay {
  const soFar = ds === today;
  const withSoFar = (note?: string) => (soFar ? [note, "so far"].filter(Boolean).join(" · ") : note);

  if (series === "steps") {
    const value = dailySteps(S, ds);
    return { ds, value, note: withSoFar(value === null ? undefined : stepSource(S, ds)) };
  }
  if (series === "active") {
    const m = activeMinutes(S, ds);
    const value = m > 0 ? m : null;
    return { ds, value, parts: value === null ? undefined : activeParts(S, ds), note: withSoFar() };
  }
  if (series === "water") {
    const n = waterEntries(S, ds).length;
    const value = n ? waterTotal(S.logs[ds]) : null;
    return { ds, value, note: withSoFar(n ? `${n} ${n === 1 ? "drink" : "drinks"}` : undefined) };
  }
  const d = ledger ?? dayLedger(S, ds, parseDay(today));
  const parts: RecordPart[] = [{ label: "In", value: d.in === null ? "not logged" : grouped(d.in) }];
  if (d.out !== null) parts.push({ label: "Out", value: grouped(d.out), est: true });
  parts.push({ label: "Work", value: grouped(d.measuredOut), est: d.measuredOut > 0 });
  const mark = d.mark === "fast" ? "fast day · left out of the averages" : d.mark === "untracked" ? "untracked · left out of the averages" : undefined;
  return { ds, value: d.in, parts, note: withSoFar(mark) };
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const mean = (xs: number[]) => (xs.length ? sum(xs) / xs.length : null);

/**
 * The window's days as weeks. `w` is 28 or 90 days ending yesterday, or 0 for everything since
 * the first logged day; today rides on top of the newest week, "so far".
 */
export function records(S: KayaState, series: RecordSeries, today: string, w: 28 | 90 | 0): Records {
  const end = addDays(today, -1);
  const from = w === 0 ? firstLogged(S, today) : addDays(today, -w);
  const span: string[] = [];
  for (let ds = from; ds <= today; ds = addDays(ds, 1)) span.push(ds);

  // Energy's averages rest on the days with both sides known and no mark; the rest on any figure.
  const ledgers = series === "energy" ? new Map(span.map((ds) => [ds, dayLedger(S, ds, parseDay(today))])) : null;
  const ledgersOf = (list: RecordDay[]) =>
    ledgers ? list.map((d) => ledgers.get(d.ds)).filter((l): l is DayLedger => l !== undefined) : [];
  const days = span.map((ds) => dayOf(S, series, ds, today, ledgers?.get(ds)));
  const counts = (d: RecordDay) => {
    if (d.ds > end) return false;
    if (ledgers) {
      const l = ledgers.get(d.ds);
      return !!l && averagable(l);
    }
    return d.value !== null;
  };

  const byWeek = new Map<string, RecordDay[]>();
  for (const d of days) {
    const k = weekKey(parseDay(d.ds));
    const list = byWeek.get(k) ?? [];
    list.push(d);
    byWeek.set(k, list);
  }

  const weeks: RecordWeek[] = [...byWeek.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([monday, list]) => {
      const used = list.filter(counts);
      const vals = used.map((d) => d.value ?? 0);
      const total = used.length ? sum(vals) : null;
      const avg = used.length ? Math.round(sum(vals) / used.length) : null;
      const week: RecordWeek = {
        from: monday,
        to: addDays(monday, 6),
        days: [...list].reverse(),
        total,
        avg,
        n: used.length,
        heading: "",
      };
      week.heading = weekHeading(series, week, ledgersOf(used));
      return week;
    });

  const used = days.filter(counts);
  const vals = used.map((d) => d.value ?? 0);
  let deficit: number | null = null;
  if (ledgers && used.length) deficit = Math.round(mean(ledgersOf(used).map((l) => (l.in ?? 0) - (l.out ?? 0))) ?? 0);

  return {
    series,
    title: SERIES_LABEL[series].title,
    unit: SERIES_LABEL[series].unit,
    weeks,
    count: used.length,
    first: span[0] ?? null,
    avg: used.length ? Math.round(sum(vals) / used.length) : null,
    total: used.length ? sum(vals) : null,
    of: span.filter((ds) => ds <= end).length,
    deficit,
  };
}

/** "Week of Mon 14 Sep · avg 7,120 · 49,840", and each series' own version of it. */
function weekHeading(series: RecordSeries, w: RecordWeek, ledgers: DayLedger[]): string {
  const head = `Week of ${dayLabel(w.from)}`;
  if (w.n === 0 || w.avg === null || w.total === null) {
    const none = { steps: "not measured", active: "nothing logged", energy: "nothing logged", water: "not logged" }[series];
    return `${head} · ${none}`;
  }
  if (series === "steps") return `${head} · avg ${grouped(w.avg)} · ${grouped(w.total)}`;
  if (series === "active") return `${head} · ${hoursMinutes(w.total)}`;
  if (series === "water") return `${head} · avg ${litres(w.avg)} L`;
  const avgOf = (f: (l: DayLedger) => number) => Math.round(mean(ledgers.map(f)) ?? 0);
  const work = avgOf((l) => l.measuredOut);
  return `${head} · In avg ${grouped(avgOf((l) => l.in ?? 0))} · Out ~${grouped(avgOf((l) => l.out ?? 0))} · Work ${
    work > 0 ? "~" : ""
  }${grouped(work)}`;
}

/* ---------- the page's words ---------- */

/** "Tue 15 Sep", or "Today · Tue 3 Nov". */
export function recordTitle(d: RecordDay, today: string): string {
  return d.ds === today ? `Today · ${dayLabel(d.ds)}` : dayLabel(d.ds);
}

/** The row's figure: "6,214", "3 h 10", "1,240", "2,150 ml"; "–" for a day with none. */
export function recordValue(series: RecordSeries, d: RecordDay): string {
  if (d.value === null) return "–";
  if (series === "active") return hoursMinutes(d.value);
  if (series === "water") return mlText(d.value);
  return grouped(d.value);
}

export function partText(p: RecordPart): string {
  return `${p.label} ${p.est ? "~" : ""}${p.value}`;
}

/** The row's subtitle: what the figure is made of, then the note; or why there is no figure. */
export function recordLine(series: RecordSeries, d: RecordDay): string {
  const parts = (d.parts ?? []).map(partText);
  if (d.value === null && series !== "energy") {
    const none = { steps: "not measured", active: "nothing logged", water: "not logged" }[series];
    return [none, d.note].filter(Boolean).join(" · ");
  }
  return [...parts, d.note].filter(Boolean).join(" · ");
}

export interface RecordsHero {
  label: string;
  figure: string | null;
  unit: string;
  line: string;
}

/** The answer above the weeks: the window's average and how much of the window it rests on. */
export function recordsHero(r: Records, w: 28 | 90 | 0): RecordsHero {
  const label = `${SERIES_LABEL[r.series].short} · ${w === 0 ? "since you started" : `${w} days`}`;
  const of = `${r.count} of ${r.of} ${r.of === 1 ? "day" : "days"}`;
  if (r.series === "steps")
    return {
      label,
      figure: r.avg === null ? null : grouped(r.avg),
      unit: "a day",
      line: r.total === null ? "No day measured yet" : `${of} measured · ${grouped(r.total)} steps in all`,
    };
  if (r.series === "active")
    return {
      label,
      figure: r.avg === null ? null : hoursMinutes(r.avg),
      unit: "an active day",
      line:
        r.total === null
          ? "No session or walk yet"
          : `${r.count} ${r.count === 1 ? "day" : "days"} with a session or a walk · ${hoursMinutes(r.total)} in all`,
    };
  if (r.series === "water")
    return {
      label,
      figure: r.avg === null ? null : litres(r.avg),
      unit: "L a day",
      line: r.total === null ? "No drink logged yet" : `${of} logged · ${drunkLitres(r.total)} L drunk in all`,
    };
  return {
    label,
    figure: r.deficit === null ? null : signed(r.deficit),
    unit: "kcal a day",
    line:
      r.deficit === null
        ? "No day with In and Out both known"
        : `${r.count} logged ${r.count === 1 ? "day" : "days"} · the deficit's average`,
  };
}

/** Under the last week: steps are measured by the phone; the other three are logged by hand. */
export function recordsNote(series: RecordSeries): string {
  return `Every day in the window, newest first. – means nothing was ${series === "steps" ? "measured" : "logged"} that day.`;
}
